import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { LogIn } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

export function Auth() {
  const { t, translateError } = useLanguage();
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  const urlType = searchParams.get('type');
  const hashType = hashParams.get('type');
  const accessToken = hashParams.get('access_token');

  const isInviteLink = (urlType === 'invite' || (accessToken && hashType === 'invite'));

  const [mode, setMode] = useState<'signin' | 'accept-invite' | 'forgot-password'>(isInviteLink ? 'accept-invite' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { signIn } = useAuth();

  useEffect(() => {
    const checkInviteLink = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const urlType = searchParams.get('type');
      const hashType = hashParams.get('type');
      const accessToken = hashParams.get('access_token');

      if (urlType === 'invite' || (accessToken && hashType === 'invite')) {
        log.debug('Invite link detected');
        setMode('accept-invite');

        if (accessToken) {
          setInviteToken(accessToken);

          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));

            if (payload.email) {
              setEmail(payload.email);
              log.debug('Email extracted from token:', payload.email);

              const { data: invitation } = await supabase
                .from('user_invitations')
                .select('role')
                .eq('email', payload.email)
                .eq('accepted', false)
                .maybeSingle();

              if (invitation?.role) {
                setRole(invitation.role);
                log.debug('Role extracted from invitation:', invitation.role);
              }
            }
          } catch (err: any) {
            log.error('Error processing invite:', err);
            setError(t('auth.error.invalidInvite'));
          }
        }
      }
    };

    checkInviteLink();
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) throw error;

      // After successful sign in, check user role and redirect appropriately
      // The App.tsx will handle the final routing, but this logs the action
      log.debug('[Auth] Sign in successful, letting router handle redirect');
    } catch (err: any) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!inviteToken) {
        throw new Error(t('auth.error.invalidToken'));
      }

      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const refreshToken = hashParams.get('refresh_token');

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: inviteToken,
        refresh_token: refreshToken || ''
      });

      if (sessionError) throw sessionError;

      const { error: updateError } = await supabase.auth.updateUser({
        password,
        data: {
          full_name: name
        }
      });

      if (updateError) throw updateError;

      const { data: invitation } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (invitation) {
        await supabase
          .from('user_invitations')
          .update({ accepted: true })
          .eq('email', email);
      }

      window.location.hash = '';
      window.location.reload();
    } catch (err: any) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      log.debug('[FORGOT-PASSWORD] Requesting password reset for:', email);
      log.debug('[FORGOT-PASSWORD] Redirect URL:', redirectUrl);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) throw error;

      log.debug('[FORGOT-PASSWORD] Email sent successfully');
      setResetEmailSent(true);
    } catch (err: any) {
      log.error('[FORGOT-PASSWORD] Error:', err);
      if (err.message.includes('rate limit')) {
        setError(t('auth.error.rateLimit'));
      } else if (err.message.includes('not found')) {
        setError(t('auth.error.noAccount'));
      } else {
        setError(translateError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  log.debug('Auth component render:', {
    mode,
    email,
    hash: window.location.hash,
    urlType,
    hashType,
    hasAccessToken: !!accessToken
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-accent-50/60 via-slate-50 to-slate-100 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] w-[480px] h-[480px] rounded-full bg-accent-200 opacity-20 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[400px] h-[400px] rounded-full bg-emerald-300 opacity-10 blur-[120px] pointer-events-none" />
      <div className="animate-rise-in relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-float ring-1 ring-gray-100 w-full max-w-md p-8">
        <div className="text-center mb-8">
          <img
            src="/logo_mohit_bw.png"
            alt="MOHIT Logo"
            className="animate-soft-pop h-16 w-auto mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900 mb-2 leading-heading">
            MOHIT Campaign HQ
          </h1>
          <p className="text-gray-600 leading-reading max-w-reading mx-auto">
            {mode === 'accept-invite' && t('auth.acceptInvite')}
            {mode === 'signin' && t('auth.signIn')}
            {mode === 'forgot-password' && t('auth.forgotPassword')}
          </p>
          {mode === 'accept-invite' && (
            <div className="mt-2 text-sm text-blue-600 font-medium leading-reading">
              {t('auth.welcomeMessage')}
            </div>
          )}
          {mode === 'forgot-password' && !resetEmailSent && (
            <div className="mt-2 text-sm text-gray-500 leading-reading">
              {t('auth.resetPasswordDescription')}
            </div>
          )}
        </div>

        {resetEmailSent ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-4 rounded-lg">
              <p className="font-medium mb-1 leading-body">{t('auth.checkYourEmail')}</p>
              <p className="text-sm leading-reading" dangerouslySetInnerHTML={{
                __html: t('auth.resetEmailSent').replace('{0}', `<strong>${email}</strong>`)
              }} />
            </div>
            <button
              type="button"
              onClick={() => {
                setMode('signin');
                setResetEmailSent(false);
                setEmail('');
              }}
              className="w-full text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              {t('auth.backToSignIn')}
            </button>
          </div>
        ) : (
          <form
            onSubmit={
              mode === 'accept-invite'
                ? handleAcceptInvite
                : mode === 'forgot-password'
                  ? handleForgotPassword
                  : handleSignIn
            }
            className="space-y-4"
          >
          {mode === 'accept-invite' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 leading-body">
                  {t('auth.name')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent leading-body"
                  placeholder={t('auth.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 leading-body">
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed leading-body"
                  placeholder={t('auth.emailPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 leading-body">
                  {t('auth.role')}
                </label>
                <input
                  type="text"
                  value={role.charAt(0).toUpperCase() + role.slice(1)}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed capitalize leading-body"
                />
              </div>
            </>
          )}

          {(mode === 'signin' || mode === 'forgot-password') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>
          )}

          {mode !== 'forgot-password' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {mode === 'accept-invite' ? t('auth.createPassword') : t('auth.password')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={mode === 'accept-invite' ? t('auth.createPasswordPlaceholder') : t('auth.passwordPlaceholder')}
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-accent-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:scale-[1.02]"
          >
            {loading ? t('auth.loading') : (
              mode === 'accept-invite' ? t('auth.completeRegistration') :
              mode === 'forgot-password' ? t('auth.sendResetLink') :
              t('auth.signInButton')
            )}
          </button>

          {mode === 'signin' && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setMode('forgot-password');
                  setError('');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {t('auth.forgotPasswordLink')}
              </button>
            </div>
          )}

          {mode === 'forgot-password' && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setMode('signin');
                  setError('');
                }}
                className="text-sm text-gray-600 hover:text-gray-700"
              >
                {t('auth.backToSignIn')}
              </button>
            </div>
          )}
        </form>
        )}

        <div className="mt-6 text-center text-sm text-gray-600">
          {t('auth.needAccess')}
        </div>
      </div>
    </div>
  );
}
